import {
  useState, useCallback, useRef, useEffect, useMemo, Suspense, lazy,
} from "react";
import {
  Box, Typography, LinearProgress, Card, CardContent, Stack, Grid, Chip, Alert,
  ToggleButtonGroup, ToggleButton, Button,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GridViewIcon from "@mui/icons-material/GridView";
import { useMutation } from "convex/react";
import { useMsal } from "@azure/msal-react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { AgentAvatar } from "../components/AgentAvatar";
import { agentColor, splitAgentName, tint } from "../utils/agentDisplay";
import type { CardPosition } from "../components/AgentOffice3D";

// ── Lazy imports (3D is heavy — load only when needed) ──────────────────────
const AgentOffice3D = lazy(() =>
  import("../components/AgentOffice3D").then((m) => ({ default: m.AgentOffice3D }))
);
const AgentOffice3DOverlay = lazy(() =>
  import("../components/AgentOffice3DOverlay").then((m) => ({ default: m.AgentOffice3DOverlay }))
);

// ── Constants ───────────────────────────────────────────────────────────────
const AGENT_COLOR_HEX = ["#00dc96", "#00b8ff", "#ff9040", "#2ca01c", "#cc44ff", "#ff4466"];
const AGENT_LABELS: Record<number, string> = {
  0: "ORCHESTRATOR", 1: "STATEMENT RETRIEVAL", 2: "BANK IMPORT",
  3: "QUICKBOOKS POSTING", 4: "INVOICE COLLECTOR", 5: "MATCHING & PROOF",
};
const AGENT_ACTIONS: Record<number, string[]> = {
  0: ["Supervising agents...", "Checking server rack...", "Delegating tasks...", "Reviewing system logs..."],
  1: ["Connecting to bank portal...", "Downloading statements...", "Authenticating API...", "Statement received!"],
  2: ["Parsing CSV data...", "Importing transactions...", "Classifying entries...", "Validating formats..."],
  3: ["Posting journal entry...", "Debiting Cash account...", "Crediting Revenue...", "Batch complete!"],
  4: ["Scanning inbox for invoices...", "OCR processing PDF...", "Extracting values...", "Invoice classified!"],
  5: ["Comparing bank vs books...", "Match found!", "Discrepancy detected...", "Generating report..."],
};

const statusColor: Record<string, "success" | "primary" | "error" | "default"> = {
  idle:       "success",
  processing: "primary",
  error:      "error",
  offline:    "default",
};

const statusGradient: Record<string, string> = {
  idle:       "linear-gradient(135deg, rgba(0,184,148,0.12), rgba(85,239,196,0.04))",
  processing: "linear-gradient(135deg, rgba(108,92,231,0.12), rgba(162,155,254,0.04))",
  error:      "linear-gradient(135deg, rgba(255,118,117,0.12), rgba(250,177,160,0.04))",
  offline:    "linear-gradient(135deg, rgba(90,94,110,0.12), rgba(90,94,110,0.04))",
};

const statusBorder: Record<string, string> = {
  idle:       "rgba(0,184,148,0.2)",
  processing: "rgba(108,92,231,0.2)",
  error:      "rgba(255,118,117,0.2)",
  offline:    "rgba(90,94,110,0.15)",
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Component ────────────────────────────────────────────────────────────────
// Canonical layout slot for each agent, derived from its name. The 3D scene
// hardwires slot 0 = Orchestrator, 1 = Retrieval, 2 = Import, 3 = QuickBooks,
// 4 = Invoice, 5 = Matching (avatar persona, monitor screen + walking logic all
// keyed by index). Convex `agentStatus.getAll` returns rows in an arbitrary
// order, so without this the HTML nameplates land on the wrong desks (e.g. the
// Orchestrator showed "Matching & Proof Agent"). Keyword match → canonical rank.
function canonicalAgentRank(name: string): number {
  const n = (name ?? "").toLowerCase();
  if (n.includes("orchestrator")) return 0;
  if (n.includes("retrieval")) return 1;
  if (n.includes("import")) return 2;
  if (n.includes("quickbooks") || n.includes("posting")) return 3;
  if (n.includes("invoice") || n.includes("collector")) return 4;
  if (n.includes("matching") || n.includes("proof")) return 5;
  return 99;
}

export function AgentStatus() {
  const agentsRaw = useStableQuery(api.agentStatus.getAll, {}, []);
  const lastActions = (useStableQuery(api.notifications.latestPerAgent, {}, {}) ?? {}) as Record<string, { summary: string; createdAt: number }>;
  const { instance, accounts } = useMsal();
  const resetCounters = useMutation(api.agentStatus.resetCounters);
  const [resetting, setResetting] = useState(false);
  const handleReset = useCallback(async () => {
    const acct = instance.getActiveAccount() ?? accounts[0];
    const email = acct?.username ?? "";
    if (!email) return;
    setResetting(true);
    try {
      await resetCounters({ reviewerEmail: email });
    } catch (e) {
      // server enforces admin — surface a minimal hint
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  }, [instance, accounts, resetCounters]);
  // Reorder to the canonical layout so avatar / monitor / nameplate all align.
  // NOTE: useStableQuery returns `undefined` during the ~6s pre-timeout window
  // (its [] fallback only fires after the timeout), so guard before spreading.
  const agents = useMemo(
    () =>
      (Array.isArray(agentsRaw) ? [...agentsRaw] : []).sort(
        (a: any, b: any) => canonicalAgentRank(a.agentName) - canonicalAgentRank(b.agentName),
      ),
    [agentsRaw],
  );

  // Same cross-cycle data as the dashboard "Reconciliation Trend" chart — drawn
  // live on the 3D office whiteboard. Ignores the global cycle filter (all cycles).
  const trendRaw = useStableQuery(api.dashboardCharts.getCycleStatusBreakdown, {}, []);
  const trendData = useMemo(() => {
    const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return (Array.isArray(trendRaw) ? trendRaw : []).map((r: any) => ({
      label: `${MONTH[(r.month - 1) % 12] ?? "?"} ${String(r.year).slice(-2)}`,
      matched: r.matched ?? 0,
      reviewQueue: r.reviewQueue ?? 0,
      unmatched: (r.unmatched ?? 0) + (r.exception ?? 0),
      posted: r.posted ?? 0,
    }));
  }, [trendRaw]);

  // View mode
  const [viewMode, setViewMode] = useState<"3d" | "grid">("3d");

  // 3D overlay state
  const [activeAgentIndex, setActiveAgentIndex] = useState(-1);
  const [cardPositions, setCardPositions] = useState<CardPosition[]>([]);
  const [toastVisible, setToastVisible]   = useState(false);
  const [toastAgentName, setToastAgentName] = useState("");
  const [toastAgentColor, setToastAgentColor] = useState("");
  const [toastAction, setToastAction]     = useState("");
  const toastIntervalRef = useRef<number | null>(null);

  // Agent activation with toast cycling
  const handleActivateAgent = useCallback((index: number) => {
    if (toastIntervalRef.current) {
      clearInterval(toastIntervalRef.current);
      toastIntervalRef.current = null;
    }

    if (activeAgentIndex === index) {
      setActiveAgentIndex(-1);
      setToastVisible(false);
      return;
    }

    setActiveAgentIndex(index);
    const actions  = AGENT_ACTIONS[index] ?? [];
    const name     = AGENT_LABELS[index] ?? "";
    const color    = AGENT_COLOR_HEX[index] ?? "#ffffff";

    setToastAgentName(name);
    setToastAgentColor(color);

    let actionIdx = 0;
    const showNext = () => {
      setToastAction(actions[actionIdx] ?? "");
      actionIdx = (actionIdx + 1) % Math.max(actions.length, 1);
    };
    showNext();
    setToastVisible(true);
    toastIntervalRef.current = window.setInterval(showNext, 2500);
  }, [activeAgentIndex]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (toastIntervalRef.current) clearInterval(toastIntervalRef.current);
    };
  }, []);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (agents === undefined) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Agent Status</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Real-time monitoring of all reconciliation agents in the pipeline
        </Typography>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading data...
        </Typography>
      </Box>
    );
  }

  const onlineCount = agents.filter((a: any) => a.status !== "offline").length;

  // ── Header (shared between both views) ─────────────────────────────────────
  const header = (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ mb: 2 }}
      flexWrap="wrap"
      gap={1}
    >
      <Box>
        <Typography variant="h4">Agent Status</Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time monitoring of all reconciliation agents in the pipeline
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
          disabled={resetting}
          onClick={handleReset}
        >
          Reset counters
        </Button>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          {onlineCount}/{agents.length} online
        </Typography>
        <Chip
          label={
            onlineCount === agents.length
              ? "All Online"
              : onlineCount === 0
              ? "All Offline"
              : "Partial"
          }
          color={
            onlineCount === agents.length
              ? "success"
              : onlineCount === 0
              ? "error"
              : "warning"
          }
          size="small"
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setViewMode(v); }}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.5,
              borderColor: "var(--c-overlay-12)",
              color: "text.secondary",
            },
            "& .Mui-selected": {
              color: "primary.main",
              borderColor: "primary.main",
            },
          }}
        >
          <ToggleButton value="3d" aria-label="3D view">
            <ViewInArIcon fontSize="small" sx={{ mr: 0.5 }} />
            3D
          </ToggleButton>
          <ToggleButton value="grid" aria-label="Grid view">
            <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} />
            Grid
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );

  // ── 3D view ────────────────────────────────────────────────────────────────
  if (viewMode === "3d") {
    return (
      <Box>
        {header}

        {/* Immersive 3D container — negative margins to go edge-to-edge */}
        <Box
          sx={{
            position:     "relative",
            mx:           -3,
            mb:           -3,
            height:       "calc(100vh - 148px)",
            minHeight:    "480px",
            background:   "#080810",
            borderRadius: "12px 12px 0 0",
            overflow:     "hidden",
          }}
        >
          <Suspense
            fallback={
              <Box
                sx={{
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  height:         "100%",
                  flexDirection:  "column",
                  gap:            2,
                }}
              >
                <LinearProgress sx={{ width: "240px" }} />
                <Typography variant="body2" color="text.secondary">
                  Loading 3D office...
                </Typography>
              </Box>
            }
          >
            {/* 3D canvas fills the container */}
            <AgentOffice3D
              agents={agents}
              activeAgentIndex={activeAgentIndex}
              onActivateAgent={handleActivateAgent}
              onCardPositionsUpdate={setCardPositions}
              trendData={trendData}
            />

            {/* HTML overlay with agent cards + toast */}
            <AgentOffice3DOverlay
              agents={agents}
              cardPositions={cardPositions}
              activeAgentIndex={activeAgentIndex}
              onActivateAgent={handleActivateAgent}
              toastVisible={toastVisible}
              toastAgentName={toastAgentName}
              toastAgentColor={toastAgentColor}
              toastAction={toastAction}
              lastActions={lastActions}
            />
          </Suspense>
        </Box>
      </Box>
    );
  }

  // ── Grid view ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {header}

      {agents.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              No agents registered. Agents will appear here once they start reporting status.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {agents.map((agent: any) => {
            const c = agentColor(agent.agentId);
            const { name, role } = splitAgentName(agent.agentName);
            return (
            <Grid item xs={12} sm={6} md={4} key={agent._id}>
              <Card
                sx={{
                  background:  `linear-gradient(135deg, ${tint(c, 0.16)} 0%, ${tint(c, 0.05)} 100%)`,
                  border:      `1px solid ${tint(c, 0.4)}`,
                  borderLeft:  `4px solid ${c}`,
                  transition:  "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform:  "translateY(-2px)",
                    boxShadow:  `0 8px 24px ${tint(c, 0.3)}`,
                  },
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 1.5 }}
                  >
                    <AgentAvatar agentId={agent.agentId} size={44} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ lineHeight: 1.1 }}>{name}</Typography>
                      {role && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {role}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={agent.status}
                      color={statusColor[agent.status] ?? "default"}
                      size="small"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Last heartbeat: {timeAgo(agent.lastHeartbeat)}
                  </Typography>

                  {agent.currentTask && (
                    <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                      Current: {agent.currentTask}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      Completed: <strong>{agent.tasksCompleted}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Failed: <strong>{agent.tasksFailed}</strong>
                    </Typography>
                  </Stack>

                  {agent.lastError && (
                    <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                      {agent.lastError}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
